import ReactDOM from "react-dom";

import { faCheck, faCircleInfo, faGear, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { Node } from "@xyflow/react";

import "@xyflow/react/dist/style.css";
import { useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { useLocation, useNavigate } from "react-router-dom";

import { CampaignDisplayStatus } from "@signalist/shared-types";

import { Icon, Modal } from "@/components";

import { Dropdown } from "@/components/Dropdown/Dropdown";

import { LeaveGuard } from "@/components/LeaveGuard/LeaveGuard";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { Toggle } from "@/components/Toggle/Toggle";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { CampaignStatus } from "@/enum/campaign-status.enum";

import { useUpdateCampaignStatusMutation } from "@/pages/Private/redux/list/list.api";

import { showGlobalToast } from "@/utils/showGlobalToast";

import { CampaignPage, CampaignPageHandle, NodeData } from "./pages/CampaignPage";
import { CampaignErrorsChip } from "./components/CampaignErrorsChip";
import { CampaignLockIcon } from "./components/CampaignStatusBadge";
import { CampaignLockBanner } from "./components/CampaignLockBanner";
import { CampaignLockModal } from "./components/CampaignLockModal";
import { useAcknowledgedCampaign } from "./utils/use-acknowledged-campaign";
import { useCampaignSettingsHandoff } from "./utils/useCampaignSettingsHandoff";
import { eventStopDatePrefill } from "./utils/eventStopDatePrefill.util";
import { useCampaignLockExplainer } from "./utils/useCampaignLockExplainer";
import { CampaignStatusPill } from "./components/CampaignStatusPill";
import { CampaignSaveStatusChip } from "./components/CampaignSaveStatusChip";
import { CampaignEditorsStrip } from "./components/CampaignEditorsStrip";
import { CampaignSaveStatus } from "./utils/useCampaignAutosave";
import { useLeaveDeletedCampaign } from "./utils/useLeaveDeletedCampaign";
import { List } from "../../schema/list";
import { EditCampaignModal } from "./components/EditCampaignModal";
import { useGlobalNodesData } from "./components/NodesContextProvider";
import { CampaignEditor, useCampaignSocket } from "../../providers/CampaignSocketContext";

export type StarterNode = Node<NodeData, "StarterNode">;

export type MyNode = Node<NodeData, "emailNode">;

/** Before `CampaignPage` has reported anything the chip renders nothing. */
const INITIAL_SAVE_STATUS: CampaignSaveStatus = {
	state: "idle",
	lastSavedAt: null,
	retry: () => undefined,
};

export const CampaignOverview = ({
	currentlySelectedCampaign,
	currentlySelectedCampaignUuid,
	handleClose,
	isOpened,
	setCurrentlySelectedCampaign,
	setCurrentlySelectedCampaignUuid,
	listData,
}: {
	isOpened: boolean;
	handleClose: () => void;
	setCurrentlySelectedCampaign: (campaign: string) => void;
	setCurrentlySelectedCampaignUuid: (campaignUuid: string) => void;
	currentlySelectedCampaign: string;
	currentlySelectedCampaignUuid: string;
	listData: List;
}) => {
	const campaignPageRef = useRef<CampaignPageHandle>(null);

	const { t } = useTranslation("translation", { keyPrefix: "list.campaignBuilder" });

	const { listData: listDataFromSockets, editors } = useCampaignSocket();

	const { eventBasedState, setEventBasedState } = useGlobalNodesData();

	const { listData: campaignListData, acknowledge } = useAcknowledgedCampaign(
		listDataFromSockets,
		listData,
		currentlySelectedCampaignUuid
	);
	const editCampaignListData = useMemo(
		() => ({ ...listData, campaignConfig: campaignListData?.campaignConfig }),
		[listData, campaignListData]
	);
	const settings = useCampaignSettingsHandoff(
		campaignPageRef,
		currentlySelectedCampaignUuid,
		isOpened,
		acknowledge
	);

	const [currentlySelectedCampaignIsValid, setCurrentlySelectedCampaignIsValid] =
		useState<boolean>(false);
	const [currentErrors, setCurrentErrors] = useState<string[]>([]);
	const [saveStatus, setSaveStatus] = useState<CampaignSaveStatus>(INITIAL_SAVE_STATUS);
	const [showRemoveDisconnectedModal, setShowRemoveDisconnectedModal] = useState<boolean>(false);

	const location = useLocation();
	const navigate = useNavigate();

	// Keep the URL in step with the selection so the builder is linkable and survives a reload. The
	// canvas is re-created by the `key` on <CampaignPage> below, not by an unmount toggle here
	// (PREC-4194).
	useEffect(() => {
		if (!location.pathname.includes("/copywriting/")) {
			return;
		}

		const params = new URLSearchParams(location.search);

		params.set("campaign", currentlySelectedCampaign);
		params.set("campaignUuid", currentlySelectedCampaignUuid);

		navigate(`${location.pathname}?${params.toString().replace(/\+/g, "%20")}`, { replace: true });
	}, [currentlySelectedCampaign, currentlySelectedCampaignUuid]);

	const campaigns = useMemo(() => {
		const listDataToUse = editCampaignListData;

		const allIds = Object.keys(listDataToUse?.campaignConfig ?? {}).filter(
			(campaign) => campaign !== "name"
		);

		return allIds.map((campaign) => {
			return {
				title: listDataToUse?.campaignConfig?.[campaign]?.name || campaign,
				isValid: listDataToUse?.campaignConfig?.[campaign]?.isValid,
				id: campaign,
			};
		});
	}, [editCampaignListData]);

	const [showAfterEventDate, setShowAfterEventDate] = useState<boolean>(false);

	const currentCampaignData = useMemo(() => {
		const listDataToUse = editCampaignListData;

		return listDataToUse?.campaignConfig?.[currentlySelectedCampaignUuid];
	}, [editCampaignListData, currentlySelectedCampaignUuid]);

	const currentCampaignTitle = currentCampaignData?.name ?? currentlySelectedCampaign;

	/** Only an event campaign has before/after-event content, so only it may be in event mode. */
	const isEventBasedCampaign = Boolean(currentCampaignData?.isEventBase);

	// Memoised because `Dropdown` re-runs its sync effect whenever `defaultValue`'s identity changes,
	// and an inline literal is a new identity on every render — the gotcha its own docblock names.
	const selectedCampaignOption = useMemo(
		() => ({ title: currentCampaignTitle, id: currentlySelectedCampaignUuid }),
		[currentCampaignTitle, currentlySelectedCampaignUuid]
	);

	// A campaign deleted while open is left, never autosaved back (PREC-4258).
	useLeaveDeletedCampaign({
		campaignUuid: currentlySelectedCampaignUuid,
		campaigns: (listDataFromSockets || listData)?.campaignConfig ? campaigns : undefined,
		saveState: saveStatus.state,
		paused: settings.isOpen,
		onLeave: (next) => {
			showGlobalToast(t("campaignDeletedElsewhere"));
			setCurrentlySelectedCampaign(next?.title ?? "");
			setCurrentlySelectedCampaignUuid(next?.id ?? "");

			if (!next) {
				handleClose();
			}
		},
	});

	// The builder's dirty signal is the autosave state PREC-4151 already lifted up here: an edit is
	// in hand while the debounce runs ("unsaved"), still in flight while it writes ("saving"), and
	// owed a retry when the server refused it ("error").
	const hasUnsavedWork =
		isOpened &&
		(saveStatus.state === "unsaved" ||
			saveStatus.state === "saving" ||
			saveStatus.state === "error");

	// Per-campaign derived status + lock (PREC-3249), keyed by campaign uuid. campaignStatuses is
	// only delivered on the REST list payload (the socket pushes campaignConfig only), so read it
	// from `listData` first and fall back to the socket copy if present.
	const campaignStatusInfo = useMemo(() => {
		return (listData?.campaignStatuses || listDataFromSockets?.campaignStatuses)?.[
			currentlySelectedCampaignUuid
		];
	}, [listData, listDataFromSockets, currentlySelectedCampaignUuid]);

	const displayStatus = campaignStatusInfo?.status;
	const isCampaignLocked = Boolean(campaignStatusInfo?.locked);

	// The lock explainer (PREC-3786). Its lead spread reuses the per-campaign stats the list already
	// serves, so a locked campaign costs one extra lazy request and no new API field.
	const lockExplainer = useCampaignLockExplainer({
		listId: listData?.id ? Number(listData.id) : undefined,
		campaignUuid: currentlySelectedCampaignUuid,
		campaignConfig: editCampaignListData?.campaignConfig,
		isLocked: isCampaignLocked,
		targetStopDate: eventStopDatePrefill(listData?.event),
	});

	// Mirror the status-column derivation (CampaignColumns.tsx): use the per-campaign derived status
	// when present, otherwise fall back to VALID/INVALID based on the campaign's validity.
	const resolvedStatus =
		displayStatus ??
		(currentlySelectedCampaignIsValid
			? CampaignDisplayStatus.VALID
			: CampaignDisplayStatus.INVALID);

	const [updateCampaignStatus, { isLoading: isTogglingStatus }] = useUpdateCampaignStatusMutation();

	const handleToggleCampaignStatus = async () => {
		if (!displayStatus) {
			return;
		}

		const nextStatus =
			displayStatus === CampaignDisplayStatus.ACTIVE
				? CampaignStatus.PAUSED
				: CampaignStatus.RUNNING;

		try {
			await updateCampaignStatus({
				id: listData.id,
				campaignUuid: currentlySelectedCampaignUuid,
				status: nextStatus,
			}).unwrap();
		} catch {
			// Surface handled globally by the RTK Query error middleware.
		}
	};

	// Once a campaign is valid the user can start assigning prospects: flush any pending canvas edits
	// (like the close button) and send them to the list's unassigned-leads tab.
	const handleGoToLeadList = () => {
		campaignPageRef.current?.flush();
		// `assignHint=campaign` triggers the "select leads" coachmark on the unassigned table (only
		// when arriving from here); `assignCampaignUuid` preselects this campaign in the reach-out
		// "Add to campaign" picker — see AssignLeadsCoachmark + ReachOutCampaignPicker.
		navigate(
			`/app/lists/detail/${listData.id}?tab=UNASSIGNED&assignHint=campaign&assignCampaignUuid=${encodeURIComponent(
				currentlySelectedCampaignUuid
			)}`
		);
	};

	// `eventBasedState` decides whether every step editor reads and writes `message`/`subject` or
	// `alternateMessage`/`alternateSubject`, so a campaign that is not event-based must never be in
	// event mode — whatever the toggle last held. The toggle only renders for an event campaign
	// (below), which is why a value carried over from one was invisible and unclearable: the user
	// had no control to turn it off, and their edits landed in fields a regular campaign never
	// sends (PREC-4193). Deriving it here makes that impossible rather than merely unlikely.
	useEffect(() => {
		setEventBasedState(isEventBasedCampaign && showAfterEventDate);
	}, [showAfterEventDate, isEventBasedCampaign]);

	// And the control itself follows the campaign: switching away resets it, so coming back to the
	// event campaign starts from "before the event date" instead of a state set for another one.
	useEffect(() => {
		setShowAfterEventDate(false);
	}, [currentlySelectedCampaignUuid]);

	const handleSetCurrentlySelectedCampaign = (campaign: string, campaignUuid: string) => {
		campaignPageRef.current?.flush();
		setCurrentlySelectedCampaign(campaign);
		setCurrentlySelectedCampaignUuid(campaignUuid);

		const params = new URLSearchParams(location.search);

		params.set("campaign", campaign);
		params.set("campaignUuid", campaignUuid);
		navigate(`${location.pathname}?${params.toString().replace(/\+/g, "%20")}`, { replace: true });
	};

	return isOpened ? (
		ReactDOM.createPortal(
			<div
				data-campaign-builder
				className="absolute top-0 left-0 w-full h-[100%] flex flex-col gap-4 p-4 z-[201]"
			>
				<div className="flex flex-col justify-start items-start w-full h-[100%] bg-white rounded-xl border border-gray-200 p-0 overflow-hidden">
					<div className="px-4 py-3 flex flex-row justify-between items-center gap-3 w-full border-b border-border-light">
						<div className="flex flex-row items-center gap-3 flex-wrap min-w-0">
							<div className="text-ssm font-semibold whitespace-nowrap">Campaign Builder</div>
							<div className="w-px h-5 bg-border-light" />
							<div className="w-[250px]">
								<Dropdown
									// Keyed by the open campaign: a pick is remembered inside the Dropdown and
									// outlives a move made for the user, such as leaving a deleted campaign.
									key={currentlySelectedCampaignUuid}
									data={campaigns.map((campaign) => ({
										title: campaign.title,
										id: campaign.id as string,
										icon: campaign?.isValid ? (
											<Icon
												className="w-4 h-4 mr-1 text-status-success bg-status-success-light rounded-full p-1"
												icon={faCheck}
											/>
										) : (
											<Icon
												className="w-4 h-4 mr-1 text-status-error bg-status-error-light rounded-full p-1"
												icon={faXmark}
											/>
										),
									}))}
									defaultValue={selectedCampaignOption}
									floating={true}
									handleSelect={(e) => {
										// Re-picking the campaign that is already open is not a hand-off, and the
										// shared Dropdown fires `handleSelect` for it like any other option. Acting
										// on it changed no state anywhere, so nothing re-rendered and nothing
										// remounted — which used to leave autosave paused for the rest of the
										// session, silently (PREC-4151).
										if (e.id === currentlySelectedCampaignUuid) {
											return;
										}

										// Save the last second of edits under the campaign they belong to before
										// the builder moves on (PREC-4026). A flush, not a pause: the builder is
										// about to be re-seeded on the new campaign, and its pending timer dies
										// with it.
										campaignPageRef.current?.flush();
										setCurrentlySelectedCampaign(e.title as string);
										setCurrentlySelectedCampaignUuid(e.id as string);
									}}
									size={ButtonSize.S}
								/>
							</div>
							{isCampaignLocked && <CampaignLockIcon onClick={lockExplainer.open} />}
							<Tooltip content={<p className="w-15 h-1">Campaign settings</p>} direction="bottom">
								<div
									className="w-8 h-8 group flex items-center justify-center cursor-pointer bg-white border border-border-light rounded-[10px] hover:border-brand-light"
									onClick={settings.open}
								>
									<Icon
										className="w-4 h-4 text-gray-500 group-hover:text-brand-light"
										icon={faGear}
									/>
								</div>
							</Tooltip>
							<CampaignSaveStatusChip status={saveStatus} />
							<CampaignStatusPill
								status={resolvedStatus}
								disabled={isTogglingStatus}
								onToggle={handleToggleCampaignStatus}
							/>
							{resolvedStatus === CampaignDisplayStatus.INVALID && (
								<CampaignErrorsChip
									errors={currentErrors}
									onRemoveDisconnected={() => setShowRemoveDisconnectedModal(true)}
								/>
							)}
							{resolvedStatus === CampaignDisplayStatus.VALID && (
								<div className="flex flex-row items-center gap-2 text-text-meta whitespace-nowrap">
									<Icon className="w-4 h-4 text-status-success" icon={faCircleInfo} />
									<span className="text-xs">{t("validInfo")}</span>
									<button
										className="text-xs font-semibold text-brand-light underline underline-offset-2 cursor-pointer hover:text-brand"
										data-testid="campaign-valid-go-to-leads"
										onClick={handleGoToLeadList}
									>
										{t("goToLeadList")}
									</button>
								</div>
							)}
							{isEventBasedCampaign && (
								<div
									className="flex flex-row items-center gap-2 text-text-meta whitespace-nowrap"
									data-testid="campaign-event-date-toggle"
								>
									<Toggle
										isChecked={showAfterEventDate}
										onChange={() => {
											setShowAfterEventDate(!showAfterEventDate);
										}}
									/>
									{showAfterEventDate ? "Show after event date" : "Show before event date"}
								</div>
							)}
						</div>
						<div className="flex flex-row items-center gap-2.5 flex-shrink-0">
							<CampaignEditorsStrip editors={editors} />
							<div
								className="w-8 h-8 flex items-center justify-center rounded-[10px] cursor-pointer hover:bg-background-light"
								onClick={() => {
									campaignPageRef.current?.flush();
									handleClose();
								}}
							>
								<Icon icon={faXmark} />
							</div>
						</div>
					</div>
					{isCampaignLocked && isOpened && (
						<div className="px-4 pt-3">
							<CampaignLockBanner onExplain={lockExplainer.open} />
						</div>
					)}
					<div className="flex w-full flex-1 min-h-0 flex-row relative">
						{isOpened && (
							// Keyed by campaign so switching gives the canvas a clean mount — the selected
							// node, the filters and the graph version all belong to the campaign that was
							// open. This replaces an isLoading toggle that unmounted and remounted the page
							// on every *open* too, running each of its mount-time queries twice (PREC-4194).
							<CampaignPage
								key={currentlySelectedCampaignUuid}
								ref={campaignPageRef}
								campaignUuid={currentlySelectedCampaignUuid}
								handleClose={handleClose}
								isLocked={isCampaignLocked}
								isOpened={isOpened}
								onLockedStructureEdit={lockExplainer.open}
								setCurrentErrors={setCurrentErrors}
								setCurrentlySelectedCampaignIsValid={setCurrentlySelectedCampaignIsValid}
								setSaveStatus={setSaveStatus}
								onCampaignSaved={acknowledge}
								title={currentlySelectedCampaign}
							/>
						)}
					</div>
				</div>
				{/* The builder saves a second after you stop typing, so leaving inside that window
				    used to lose the edit with nothing said (PREC-4194). This covers in-app
				    navigation — the Stepper's tabs, the sidebar — and closing the tab. */}
				<LeaveGuard
					body={t("leaveBody")}
					discardLabel={t("leaveDiscard")}
					onDiscard={() => undefined}
					onSave={async () => (await campaignPageRef.current?.flush()) ?? true}
					saveLabel={t("leaveSave")}
					title={t("leaveTitle")}
					when={hasUnsavedWork}
				/>
				<CampaignLockModal
					campaignName={currentlySelectedCampaign}
					isDuplicating={lockExplainer.isDuplicating}
					isOpened={lockExplainer.isOpened}
					leadStates={lockExplainer.leadStates}
					onClose={lockExplainer.close}
					onDuplicate={lockExplainer.duplicate}
				/>
				{/* Mounted per opening and keyed by the campaign: the dialog seeds its fields when it
				    mounts. Mounted for the whole session, it kept the campaign the builder was opened on
				    and saved that one's name, timezone and stop date over the campaign open now
				    (PREC-4432). The `key` on <CampaignPage> resets the canvas only. */}
				{settings.isOpen && (
					<EditCampaignModal
						key={currentlySelectedCampaignUuid}
						currentCampaign={currentCampaignTitle}
						currentCampaignUuid={currentlySelectedCampaignUuid}
						handleClose={settings.dismiss}
						onSaved={settings.saved}
						isOpened={settings.isOpen}
						listData={editCampaignListData}
						setCurrentlySelectedCampaign={handleSetCurrentlySelectedCampaign}
					/>
				)}
				{showRemoveDisconnectedModal && (
					<Modal
						handleClose={() => setShowRemoveDisconnectedModal(false)}
						handleSave={() => {
							campaignPageRef.current?.removeDisconnectedNodes();
							setShowRemoveDisconnectedModal(false);
						}}
						isOpened={showRemoveDisconnectedModal}
						submitButtonColor={ButtonColor.DANGER}
						submitButtonText="Remove"
						title="Remove disconnected nodes"
					>
						<div className="text-sm">
							This will permanently remove all disconnected nodes from the campaign. This action
							cannot be undone. Are you sure you want to continue?
						</div>
					</Modal>
				)}
			</div>,
			document.body
		)
	) : (
		<></>
	);
};
